<template>
  <div>
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky tb-no-print">
        <div class="fin-toolbar">
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="backToCatalog">
              <v-icon small>arrow_back</v-icon>
            </v-btn>
            <span>{{ $t("reports.backToCatalog") }}</span>
          </v-tooltip>
          <span class="fin-title">{{ title }}</span>
          <v-spacer></v-spacer>
          <template v-if="canExport">
            <v-tooltip bottom>
              <v-btn slot="activator" icon small flat @click="exportExcel">
                <v-icon small>grid_on</v-icon>
              </v-btn>
              <span>Excel</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-btn slot="activator" icon small flat @click="downloadCsv">
                <v-icon small>file_download</v-icon>
              </v-btn>
              <span>CSV</span>
            </v-tooltip>
            <v-tooltip bottom>
              <v-btn slot="activator" icon small flat @click="reportExportPrint">
                <v-icon small>print</v-icon>
              </v-btn>
              <span>{{ $t("reports.print") }}</span>
            </v-tooltip>
          </template>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="load">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("reports.refresh") }}</span>
          </v-tooltip>
        </div>

        <div v-if="filterDefs.length" class="fin-filters">
          <report-filter-field
            v-for="definition in filterDefs"
            :key="definition.key"
            :definition="definition"
            :value="filters[definition.key]"
            @input="onFilterInput(definition.key, $event)"
          />
          <v-btn small color="primary" class="report-page__apply" @click="applyFilters">
            {{ $t("reports.apply") }}
          </v-btn>
        </div>
      </div>

      <v-divider></v-divider>

      <div v-if="description" class="report-page__description">{{ description }}</div>

      <!-- Warnings come first: they change how every number below must be read. -->
      <v-alert
        v-for="(warning, index) in warnings"
        :key="'w' + index"
        :value="true"
        type="warning"
        outline
        class="report-page__alert"
      >
        {{ warning.ar || warning.en }}
      </v-alert>

      <!-- A blocked report never shows a table. Explaining the absence IS the
           deliverable; a zero here would read as an answer. -->
      <div v-if="blocked" class="report-page__blocked">
        <div class="report-page__blocked-head">
          <v-icon color="grey darken-1">info_outline</v-icon>
          <span>{{ $t("reports.blockedHeading") }}</span>
        </div>
        <div class="report-page__blocked-label">{{ $t("reports.blockedMissing") }}</div>
        <p>{{ blocked.missing_data && blocked.missing_data.ar }}</p>
        <div class="report-page__blocked-label">{{ $t("reports.blockedRecommendation") }}</div>
        <p>{{ blocked.recommendation && blocked.recommendation.ar }}</p>
      </div>

      <template v-else>
        <div v-if="summaryTiles.length" class="report-page__tiles">
          <div v-for="tile in summaryTiles" :key="tile.key" class="report-page__tile">
            <div class="report-page__tile-label">{{ tile.label }}</div>
            <div class="report-page__tile-value">{{ tile.value }}</div>
          </div>
        </div>

        <report-table
          v-for="(table, index) in tables"
          :key="index"
          :title="table.title"
          :columns="table.columns"
          :rows="table.rows"
          :totals="table.totals"
          :sort-by="listing.sortBy"
          :descending="listing.descending"
          @sort="onSort"
        />

        <div v-if="pagination" class="report-page__pagination tb-no-print">
          <v-btn small flat :disabled="listing.page <= 1" @click="goToPage(listing.page - 1)">
            {{ $t("reports.previous") }}
          </v-btn>
          <span class="report-page__page-label">
            {{ listing.page }} / {{ pagination.last_page }}
            <span class="report-page__page-total">({{ pagination.total }})</span>
          </span>
          <v-btn
            small
            flat
            :disabled="listing.page >= pagination.last_page"
            @click="goToPage(listing.page + 1)"
          >
            {{ $t("reports.next") }}
          </v-btn>
        </div>
      </template>

      <!-- Notes last: caveats about which clock, which ledger, and what a zero
           means here. They are printed with the report and in every export. -->
      <div v-if="notes.length" class="report-page__notes">
        <div class="report-page__notes-title">{{ $t("reports.notesTitle") }}</div>
        <ul>
          <li v-for="(note, index) in notes" :key="'n' + index">{{ note.ar || note.en }}</li>
        </ul>
      </div>

      <div v-if="generatedAt" class="report-page__stamp">
        {{ $t("reports.generatedAt") }}: {{ generatedAt }}
      </div>
    </v-card>
  </div>
</template>

<script>
import LoadingComponent from "@/components/LoadingComponent";
import ReportFilterField from "@/components/reports/ReportFilterField";
import ReportTable from "@/components/reports/ReportTable";
import reportExport from "@/mixins/reportExport";

/*
 * The single page that renders every report in the catalog (requirements doc
 * 2026-09-09).
 *
 * It knows nothing about any specific report: filters, columns, units,
 * totals, warnings and notes all arrive as metadata from the report
 * definition. Adding a report to the product is adding a PHP class and one
 * line in ReportCatalog -- no frontend change at all.
 *
 * The page is intentionally NOT in menu.js. The dashboard's own permission
 * helper returns true for any route it does not know about, so the real gate
 * is the server's report_permission middleware; a 403 is rendered here as an
 * explanation rather than a broken screen.
 */
export default {
  name: "ReportPage",
  components: { LoadingComponent, ReportFilterField, ReportTable },
  mixins: [reportExport],
  data() {
    return {
      loading: false,
      report: null,
      filterDefs: [],
      filters: {},
      listing: { page: 1, sortBy: "", descending: false },
    };
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    title() {
      return this.report ? this.report.title_ar : this.$t("reports.pageTitle");
    },
    description() {
      return this.report ? this.report.description_ar : "";
    },
    warnings() {
      return this.report ? this.report.warnings || [] : [];
    },
    notes() {
      return this.report ? this.report.notes || [] : [];
    },
    blocked() {
      return this.report ? this.report.blocked : null;
    },
    pagination() {
      return this.report ? this.report.pagination : null;
    },
    generatedAt() {
      return this.report ? this.report.generated_at : "";
    },
    tables() {
      return this.report ? this.reportTables(this.report) : [];
    },
    canExport() {
      return !!this.report && !this.blocked;
    },
    /**
     * Headline figures for the dashboard-style reports.
     *
     * THE UNIT IS WHAT MAKES A TILE HONEST. Money is stored in old-unit SYP
     * and has to be divided by 100; a percentage, a count and a duration must
     * not be. A tile the server did not describe with a unit is therefore not
     * rendered at all: a stored amount shown raw is a hundred times too large,
     * and a figure that size read out in a management meeting is worse than a
     * tile that is simply absent -- every such number already appears,
     * correctly typed, in the report's own tables below.
     */
    summaryTiles() {
      const summary = (this.report && this.report.summary) || {};
      const declared = Array.isArray(summary)
        ? summary
        : Object.keys(summary).map(function (key) {
            const entry = summary[key];
            const isTile = entry && typeof entry === "object" && !Array.isArray(entry);
            return isTile ? Object.assign({ key: key }, entry) : { key: key, value: entry };
          });

      return declared
        .filter(function (tile) {
          return !!(tile && tile.unit);
        })
        .map(function (tile, index) {
          return {
            key: tile.key || "tile-" + index,
            label: tile.label_ar || tile.label_en || tile.key,
            // `display` is the server's own rendering when it sends one; the
            // unit-aware formatter is the fallback, so a tile and the column
            // it summarises can never be formatted two different ways.
            value:
              tile.display === undefined || tile.display === null
                ? this.reportCellText({ unit: tile.unit }, tile.value)
                : tile.display,
          };
        }, this);
    },
  },
  watch: {
    // The catalog can route straight from one report to another.
    slug() {
      this.filters = {};
      this.listing = { page: 1, sortBy: "", descending: false };
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    /*
     * Every finance page in this dashboard carries its own copy: the helper in
     * main.js is module-private, so calling it as a method threw a TypeError
     * inside the catch handler and the failure -- a 403 on a report the user
     * may not open above all -- reached the operator as a blank page with no
     * toast at all.
     */
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    load() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.REPORT(this.slug), { params: this.buildParams() })
        .then(
          function (response) {
            this.report = response.data.data;
            this.filterDefs = this.report.filter_defs || [];
            // Adopt the server's normalized filters so the controls show the
            // defaults it actually applied (relative dates included) rather
            // than sitting empty.
            if (!Object.keys(this.filters).length) {
              this.filters = Object.assign({}, this.report.filters || {});
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            const status = error.response && error.response.status;
            const fallback =
              status === 403
                ? this.$t("reports.noPermission")
                : this.$t("reports.failedToLoad");
            window.getApp.$emit("SHOW_TOAST", this.getErrorMessage(error, fallback), "red");
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    buildParams(extra) {
      const params = {};
      Object.keys(this.filters).forEach((key) => {
        const value = this.filters[key];
        if (value !== null && value !== undefined && value !== "") {
          params[key] = Array.isArray(value) ? value.join(",") : value;
        }
      });
      if (this.listing.sortBy) {
        params.sortBy = this.listing.sortBy;
        params.descending = this.listing.descending;
      }
      if (this.pagination) {
        params.page = this.listing.page;
      }
      return Object.assign(params, extra || {});
    },
    onFilterInput(key, value) {
      this.$set(this.filters, key, value);
    },
    applyFilters() {
      this.listing.page = 1;
      this.load();
    },
    onSort(event) {
      this.listing.sortBy = event.sortBy;
      this.listing.descending = event.descending;
      // Back to page 1, for the same reason a filter change is: re-sorting
      // while sitting on page 5 hands the reader the middle of a different
      // ordering and reads as the sort having done nothing.
      this.listing.page = 1;
      this.load();
    },
    goToPage(page) {
      this.listing.page = page;
      this.load();
    },
    backToCatalog() {
      this.$router.push({ name: "ReportCatalog" });
    },
    exportExcel() {
      if (!this.report) {
        return;
      }
      this.reportExportExcel(this.report).catch(
        function () {
          window.getApp.$emit("SHOW_TOAST", this.$t("reports.failedExport"), "red");
        }.bind(this)
      );
    },
    downloadCsv() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.REPORT(this.slug), {
          params: this.buildParams({ format: "csv" }),
          responseType: "blob",
        })
        .then(
          function (response) {
            const blob = new Blob([response.data], {
              type: response.headers["content-type"] || "text/csv",
            });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = this.slug + "-" + this.reportFileStamp() + ".csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("reports.failedExport")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
  },
};
</script>

<!-- Shared compact finance styling. Imported directly because this page renders
     no <finance-section-nav />, which is what carries it on the finance pages:
     without it every fin-* class here (the sticky header, the toolbar, the
     filter row) would resolve to nothing. -->
<style src="@/assets/finance-compact.css"></style>

<style scoped>
.report-page__apply {
  margin-inline-start: 8px;
}
.report-page__description {
  padding: 10px 12px 0;
  font-size: 12px;
  opacity: 0.75;
  line-height: 1.7;
}
.report-page__alert {
  margin: 10px 12px;
  font-size: 12px;
}
.report-page__blocked {
  padding: 16px 20px 24px;
  font-size: 13px;
  line-height: 1.9;
}
.report-page__blocked-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 12px;
}
.report-page__blocked-label {
  font-weight: 700;
  margin-top: 10px;
}
.report-page__tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px;
}
.report-page__tile {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 140px;
}
.report-page__tile-label {
  font-size: 11px;
  opacity: 0.7;
}
.report-page__tile-value {
  font-size: 18px;
  font-weight: 700;
  margin-top: 2px;
}
.report-page__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
}
.report-page__page-label {
  font-size: 12px;
}
.report-page__page-total {
  opacity: 0.6;
}
.report-page__notes {
  padding: 12px 16px 4px;
  font-size: 12px;
  line-height: 1.9;
  opacity: 0.85;
}
.report-page__notes-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.report-page__stamp {
  padding: 4px 16px 14px;
  font-size: 11px;
  opacity: 0.6;
}
/* The print button is one of the three exports this page offers, so the rule
   that honours `tb-no-print` has to live here: the only other copy is scoped
   to the trial-balance page and never applied to these elements. */
@media print {
  .tb-no-print {
    display: none !important;
  }
  .report-page__alert,
  .report-page__blocked,
  .report-page__notes {
    page-break-inside: avoid;
  }
}
</style>
